import { useMemo } from "react";
import { DataTable, type RowAction } from "../ui/data-table";
import { userColumns } from "./user-columns";
import { Users, Edit, KeyRound, Mail, Check, X, Ban, Unlock, Trash2 } from "lucide-react";
import type { User } from "../../types/api";
import type { PresenceMap } from "../../types/data-table";

interface UserTableProps {
  users: User[];
  isLoading?: boolean;
  presence?: PresenceMap;
  currentInstanceId?: string;
  onEdit: (user: User) => void;
  onResetPassword: (userId: number) => void;
  onResendInvite: (userId: number) => void;
  onApprove: (userId: number) => void;
  onReject: (userId: number) => void;
  onDisable: (userId: number) => void;
  onEnable: (userId: number) => void;
  onDelete: (userId: number) => void;
}

export function UserTable({
  users,
  isLoading = false,
  presence,
  currentInstanceId,
  onEdit,
  onResetPassword,
  onResendInvite,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  onDelete,
}: UserTableProps) {
  const actions = useMemo<RowAction<User>[]>(
    () => [
      {
        label: "Edit User",
        icon: Edit,
        onClick: (user) => onEdit(user),
      },
      {
        label: "Reset Password",
        icon: KeyRound,
        onClick: (user) => onResetPassword(user.user_id),
        visible: (user) => user.registration_status === "active",
      },
      {
        label: "Resend Invite",
        icon: Mail,
        onClick: (user) => onResendInvite(user.user_id),
        visible: (user) => user.registration_status === "invited",
      },
      {
        label: "Approve",
        icon: Check,
        onClick: (user) => onApprove(user.user_id),
        visible: (user) => user.registration_status === "pending_approval",
        separatorBefore: true,
      },
      {
        label: "Reject",
        icon: X,
        onClick: (user) => onReject(user.user_id),
        visible: (user) => user.registration_status === "pending_approval",
      },
      {
        label: "Disable",
        icon: Ban,
        onClick: (user) => onDisable(user.user_id),
        visible: (user) => user.registration_status === "active",
        separatorBefore: true,
      },
      {
        label: "Enable",
        icon: Unlock,
        onClick: (user) => onEnable(user.user_id),
        visible: (user) => user.registration_status === "disabled",
        separatorBefore: true,
      },
      {
        label: "Delete",
        icon: Trash2,
        onClick: (user) => onDelete(user.user_id),
        variant: "destructive",
        separatorBefore: true,
      },
    ],
    [onEdit, onResetPassword, onResendInvite, onApprove, onReject, onDisable, onEnable, onDelete]
  );

  return (
    <DataTable
      columns={userColumns}
      data={users}
      getRowId={(user) => String(user.user_id)}
      isLoading={isLoading}
      emptyIcon={Users}
      emptyTitle="No users found"
      emptyDescription="Try adjusting your filters or search query"
      presence={presence}
      presenceKey={(user) => user.user_id}
      currentInstanceId={currentInstanceId}
      actions={actions}
      virtualize={false}
    />
  );
}
